import type { ActivityCounts, ActivityQuery, ActivityReport, Me } from '@smarton-pbx/shared';
import { ArrowDownLeft, ArrowUpRight, Clock, PhoneCall, PhoneMissed } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { Loading, Notice, PageHeader } from '../components/ui';
import { locale, t, tc } from '../i18n';
import { useLoad } from '../lib/hooks';
import { QueueReportSection } from './queues';

function talkTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h ? t('{h} h {m} min', { h, m }) : t('{n} min', { n: m });
}

function bucketLabel(day: string, period: 'day' | 'week'): string {
  const date = new Date(`${day}T12:00:00Z`);
  const label = date.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return period === 'week' ? t('Week of {date}', { date: label }) : `${date.toLocaleDateString(locale, { weekday: 'short', timeZone: 'UTC' })} ${label}`;
}

const ranges = {
  day: [
    { days: 7, label: t('Last 7 days') },
    { days: 30, label: t('Last 30 days') },
  ],
  week: [
    { days: 8 * 7, label: t('Last 8 weeks') },
    { days: 26 * 7, label: t('Last 26 weeks') },
  ],
} as const;

function fromDate(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days + 1);
  return date.toLocaleDateString('sv');
}

type PersonActivity = ActivityReport['people'][number];

/** Each person's calls in the period; admins see their region too. */
function activityColumns(showRegion: boolean): Column<PersonActivity>[] {
  const count = (key: 'incoming' | 'outgoing' | 'answered', label: string): Column<PersonActivity> => ({
    key,
    label,
    className: 'num tabular',
    cell: (person) => person.totals[key],
    value: (person) => person.totals[key],
  });
  return [
    {
      key: 'person',
      label: t('Person'),
      fixed: true,
      cell: (person) => (
        <>
          <Link to={`/calls?userId=${person.userId}`} className="plain-link" title={t('Their calls')}>
            {person.name}
          </Link>
          {person.regionName && showRegion && <span className="subtle" style={{ display: 'block', fontSize: '0.8rem' }}>{person.regionName}</span>}
        </>
      ),
      excel: [
        { label: t('Person'), value: (person) => person.name },
        ...(showRegion ? [{ label: t('Region'), value: (person: PersonActivity) => person.regionName }] : []),
      ],
    },
    count('incoming', tc('count', 'Incoming')),
    count('outgoing', tc('count', 'Outgoing')),
    count('answered', tc('count', 'Answered')),
    {
      key: 'missed',
      label: tc('count', 'Missed'),
      className: 'num tabular',
      cell: (person) => <span className={person.totals.missed ? 'danger-text' : undefined}>{person.totals.missed}</span>,
      value: (person) => person.totals.missed,
    },
    {
      key: 'talk',
      label: t('Talk time'),
      className: 'num tabular',
      cell: (person) => talkTime(person.totals.talkSeconds),
      // Minutes, for sums in Excel.
      excel: [{ label: `${t('Talk time')} (min)`, value: (person) => Math.round(person.totals.talkSeconds / 60) }],
    },
  ];
}

export function Dashboard({ me }: { me: Me }) {
  const [period, setPeriod] = useState<'day' | 'week'>('day');
  const [days, setDays] = useState<number>(7);
  const [regionId, setRegionId] = useState('');
  const regions = useLoad(() => (me.role === 'TENANT_ADMIN' ? api.listRegions() : Promise.resolve([])), [me.role]);
  const query: ActivityQuery = { period, from: fromDate(days), ...(regionId && { regionId }) };
  const report = useLoad(() => api.activity(query), [period, days, regionId]);
  const table = useTable(
    'activity',
    activityColumns(me.role === 'TENANT_ADMIN'),
    mayDownload(me.role) ? { name: t('Dashboard'), rows: () => report.data?.people ?? [] } : null,
  );

  const tiles: { label: string; icon: typeof PhoneCall; value: (c: ActivityCounts) => string }[] = [
    { label: 'Incoming', icon: ArrowDownLeft, value: (c) => String(c.incoming) },
    { label: 'Outgoing', icon: ArrowUpRight, value: (c) => String(c.outgoing) },
    { label: 'Answered', icon: PhoneCall, value: (c) => String(c.answered) },
    { label: 'Missed', icon: PhoneMissed, value: (c) => String(c.missed) },
    { label: 'Talk time', icon: Clock, value: (c) => talkTime(c.talkSeconds) },
  ];

  const data = report.data;
  const peak = data ? Math.max(1, ...data.buckets.map((b) => (data.byPeriod[b]?.incoming ?? 0) + (data.byPeriod[b]?.outgoing ?? 0))) : 1;

  return (
    <div className="stack">
      <PageHeader
        title={t('Dashboard')}
        description={me.role === 'AGENT' ? t('Your calls, by day or week.') : me.role === 'REGION_MANAGER' ? t('Calls per person in your region.') : t('Calls per person, across regions.')}
      />
      <div className="card filters">
        <div className="segmented" role="group" aria-label={t('Group by')}>
          {(['day', 'week'] as const).map((p) => (
            <button
              key={p}
              type="button"
              className={period === p ? 'active' : ''}
              aria-pressed={period === p}
              onClick={() => {
                setPeriod(p);
                setDays(ranges[p][0].days);
              }}
            >
              {p === 'day' ? t('By day') : t('By week')}
            </button>
          ))}
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t('Period')}>
          {ranges[period].map((r) => (
            <option key={r.days} value={r.days}>
              {r.label}
            </option>
          ))}
        </select>
        {me.role === 'TENANT_ADMIN' && (regions.data?.length ?? 0) > 1 && (
          <select value={regionId} onChange={(e) => setRegionId(e.target.value)} aria-label={t('Region')}>
            <option value="">{t('All regions')}</option>
            {regions.data?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
        {data && <span className="subtle grow" style={{ textAlign: 'right', fontSize: '0.85rem' }}>{t('Times in {zone}', { zone: data.timeZone.replace(/_/g, ' ') })}</span>}
      </div>
      {report.error && <Notice kind="error">{report.error}</Notice>}
      {!data ? (
        <Loading />
      ) : (
        <>
          <div className="stat-tiles">
            {tiles.map(({ label, icon: Icon, value }) => (
              <div key={label} className="card tile" data-testid={`total-${label.toLowerCase().replace(' ', '-')}`}>
                <span className="tile-label">
                  <Icon size={15} aria-hidden /> {tc('count', label)}
                </span>
                <span className="tile-value tabular">{value(data.totals)}</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <h2>{period === 'day' ? t('Calls per day') : t('Calls per week')}</h2>
              <span className="legend">
                <span className="swatch incoming" /> {tc('count', 'Incoming')} <span className="swatch outgoing" /> {tc('count', 'Outgoing')}
              </span>
            </div>
            <div className="card-body">
              {data.totals.incoming + data.totals.outgoing === 0 && (
                <p className="subtle" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                  {t('No calls in this period.')}
                </p>
              )}
              <div className="bars" role="img" aria-label={period === 'day' ? t('Calls per day') : t('Calls per week')}>
                {data.buckets.map((bucket) => {
                  const counts = data.byPeriod[bucket];
                  const incoming = counts?.incoming ?? 0;
                  const outgoing = counts?.outgoing ?? 0;
                  return (
                    <div
                      key={bucket}
                      className="bar"
                      title={t('{period}: {incoming} incoming, {outgoing} outgoing, {missed} missed', { period: bucketLabel(bucket, period), incoming, outgoing, missed: counts?.missed ?? 0 })}
                    >
                      <div className="stack-bar">
                        <span className="outgoing" style={{ height: `${(outgoing / peak) * 100}%` }} />
                        <span className="incoming" style={{ height: `${(incoming / peak) * 100}%` }} />
                      </div>
                      <span className="bar-label">
                        {new Date(`${bucket}T12:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <QueueReportSection period={period} from={query.from!} canDownload={mayDownload(me.role)} />

          <div className="card table-wrap">
            <table>
              <TableHead table={table} />
              <tbody>
                {data.people.map((person) => (
                  <tr key={person.userId} data-testid="activity-row" data-user-id={person.userId}>
                    <Cells table={table} row={person} />
                    <td className="actions" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
